/* eslint-disable react/display-name */
import React, { FC, useState } from "react";
import { Box, IconButton, Typography } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import BaseDataGrid from "../../../components/modules/BaseDataGrid";
import Loader from "../../../components/elements/Loader";
import classes from "./EmailsDashboard.module.scss";
import { EmailsToHash } from "../../../types/campaignTypes";

type EmailsDashboardProps = {
  emailsToHash: EmailsToHash[];
  areEmailsLoading: boolean;
};

const EmailsDashboard: FC<EmailsDashboardProps> = ({ emailsToHash, areEmailsLoading }) => {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const hasGeneralDomain = emailsToHash.some(({ domain }) => domain === "general");

  const toggleExpand = (domain: string) => {
    setExpandedDomains((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(domain)) {
        newSet.delete(domain);
      } else {
        newSet.add(domain);
      }
      return newSet;
    });
  };

  const columns = [
    {
      field: "name",
      headerName: "Domain",
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center">
          {params.row.isDomain && (
            <IconButton size="small" onClick={() => toggleExpand(params.row.name)}>
              {expandedDomains.has(params.row.name) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          <Typography>{params.row.name}</Typography>
        </Box>
      ),
    },
  ];

  const rows = emailsToHash
    .sort((a, b) => a.domain.localeCompare(b.domain))
    .flatMap(({ domain, emails }, index) => {
      const isExpanded = expandedDomains.has(domain);

      const domainRow = {
        id: `domain-${index}`,
        name: domain,
        isDomain: true,
      };

      const emailRows = isExpanded
        ? emails
            .sort((a, b) => a.localeCompare(b))
            .map((email, idx) => ({
              id: `email-${domain}-${idx}`,
              name: email,
              isDomain: false,
            }))
        : [];

      return [domainRow, ...emailRows];
    });

  return (
    <div className={classes.emailsDashboardContainer}>
      {areEmailsLoading && (
        <div className={classes.loaderContainer}>
          <Typography>We're getting your company information. This may take a few minutes...</Typography>
          <Loader isAbsolutePosition={false} size={42} />
        </div>
      )}
      {rows.length > 0 && !areEmailsLoading && (
        <div className={classes.dashboardContainer}>
          {hasGeneralDomain && (
            <Typography className={classes.infoText}>
              For CRM targeting, the minimum number of emails per domain is <strong>20</strong>. If this threshold is
              not met, emails are grouped under a general domain for targeting purposes.
            </Typography>
          )}

          <BaseDataGrid columns={columns} rows={rows} autoHeight disableSelectionOnClick />
        </div>
      )}
    </div>
  );
};

export default EmailsDashboard;
